import React, { useMemo } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CATALOG, getCatalogEntry } from "@/data/vipSimulatorCatalog";
import { weaponShowcaseImageUrl } from "@/data/iconAssets";
import { attachmentFitsWeapon, getAttachmentSlotsForWeapon } from "@/data/weaponAttachments";
import { getWeaponSketchfabEmbed } from "@/data/weaponSketchfab";
import { getWeaponKeyFromCatalogEntry } from "@/data/weaponSpecs";
import { translate, useI18n } from "@/i18n/I18nProvider";
import { findWeaponPlacement } from "./vipSimulatorMoves";
import LazyCatalogImage from "./LazyCatalogImage";
import WeaponSketchfabViewer from "./WeaponSketchfabViewer";

const SLOT_KEYS = new Set(["sight", "barrel", "laser", "magazine"]);

/** Rótulo do slot de acessório no idioma atual. */
function slotLabel(slot, lang) {
    return SLOT_KEYS.has(slot) ? translate(`simulator.wmod.slot.${slot}`, null, lang) : slot;
}

function findPoolPlacement(sim, catalogId) {
    for (const category of Object.keys(sim.pool)) {
        for (const p of sim.pool[category] || []) {
            if (p?.catalogId === catalogId) return p;
        }
    }
    return { instanceId: `pool:${catalogId}`, catalogId, quantity: 1 };
}

function PoolAttachmentCard({ placement, entry, equippedSlot, onEquip, onUnequipEquipped }) {
    const { t, item } = useI18n();
    const isEquipped = Boolean(equippedSlot);
    const displayName = item(entry.name);

    const handleClick = () => {
        if (isEquipped) {
            onUnequipEquipped(equippedSlot);
        } else {
            onEquip(placement.instanceId);
        }
    };

    return (
        <button
            type="button"
            className={`vip-sim-wmod-pool-item ${isEquipped ? "vip-sim-wmod-pool-item--equipped" : ""}`}
            title={
                isEquipped
                    ? t("simulator.wmod.poolEquippedTitle", { name: displayName })
                    : t("simulator.wmod.poolEquipTitle", { name: displayName })
            }
            onClick={handleClick}
        >
            <LazyCatalogImage
                src={entry.icon}
                fallbacks={entry.iconCandidates}
                className="vip-sim-wmod-pool-item__img"
                eager
            />
            <span className="vip-sim-wmod-pool-item__name">{displayName}</span>
            {isEquipped ? (
                <span className="vip-sim-wmod-pool-item__badge">{t("simulator.wmod.equippedBadge")}</span>
            ) : null}
        </button>
    );
}

function EquippedSlot({ slot, placement, entry, onUnequip }) {
    const { t, item, lang } = useI18n();
    return (
        <button
            type="button"
            className={`vip-sim-wmod-slot ${placement ? "vip-sim-wmod-slot--filled" : ""}`}
            title={
                placement
                    ? t("simulator.wmod.slotFilledTitle", { name: item(entry?.name) })
                    : t("simulator.wmod.slotEmptyTitle", { slot: slotLabel(slot, lang) })
            }
            onClick={() => placement && onUnequip(slot)}
            disabled={!placement}
        >
            <span className="vip-sim-wmod-slot__label">{slotLabel(slot, lang)}</span>
            {placement && entry ? (
                <LazyCatalogImage
                    src={entry.icon}
                    fallbacks={entry.iconCandidates}
                    className="vip-sim-wmod-slot__img"
                    eager
                />
            ) : (
                <span className="vip-sim-wmod-slot__empty">—</span>
            )}
        </button>
    );
}

export default function WeaponCustomizeModal({
    open,
    weaponInstanceId,
    sim,
    onClose,
    onEquipFromPool,
    onUnequipSlot,
}) {
    const { t, item, lang } = useI18n();
    const weapon = useMemo(() => {
        if (!weaponInstanceId || !sim) return null;
        return findWeaponPlacement(sim, weaponInstanceId);
    }, [sim, weaponInstanceId]);

    const entry = weapon ? getCatalogEntry(weapon.catalogId) : null;
    const weaponKey = entry ? getWeaponKeyFromCatalogEntry(entry) : null;
    const slots = weaponKey ? getAttachmentSlotsForWeapon(weaponKey) : [];
    const sketchfabEmbed = weaponKey ? getWeaponSketchfabEmbed(weaponKey) : null;
    const showcaseSrc =
        !sketchfabEmbed && entry ? weaponShowcaseImageUrl(entry.iconFile || entry.weaponKey) : "";

    const poolAttachments = useMemo(() => {
        if (!weaponKey || !sim) return [];
        return CATALOG.filter((e) => e.cat === "attachment" && attachmentFitsWeapon(e, weaponKey))
            .map((entry) => ({
                entry,
                placement: findPoolPlacement(sim, entry.id),
            }))
            .sort((a, b) => item(a.entry.name).localeCompare(item(b.entry.name), lang));
    }, [sim, weaponKey, item, lang]);

    const equippedByCatalogId = useMemo(() => {
        const map = {};
        if (!weapon?.attachments) return map;
        for (const [slot, p] of Object.entries(weapon.attachments)) {
            if (p) map[p.catalogId] = slot;
        }
        return map;
    }, [weapon]);

    if (!entry || !weapon) return null;

    const attachments = weapon.attachments || {};

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="vip-sim-wmod-dialog">
                <DialogHeader>
                    <DialogTitle className="vip-sim-wmod-dialog__title">{item(entry.name)}</DialogTitle>
                    <DialogDescription className="vip-sim-wmod-dialog__desc">
                        {sketchfabEmbed
                            ? t("simulator.wmod.desc3d", { title: sketchfabEmbed.title })
                            : t("simulator.wmod.descNo3d")}
                    </DialogDescription>
                </DialogHeader>

                <div className="vip-sim-wmod-layout">
                    <div
                        className={`vip-sim-wmod-showcase ${sketchfabEmbed ? "vip-sim-wmod-showcase--sketchfab" : ""}`}
                    >
                        {sketchfabEmbed ? (
                            <WeaponSketchfabViewer embed={sketchfabEmbed} active={open} />
                        ) : (
                            <div className="vip-sim-wmod-showcase__stage">
                                {showcaseSrc ? (
                                    <img
                                        src={showcaseSrc}
                                        alt=""
                                        className="vip-sim-wmod-showcase__weapon"
                                        draggable={false}
                                    />
                                ) : (
                                    <img
                                        src={entry.icon}
                                        alt=""
                                        className="vip-sim-wmod-showcase__weapon"
                                        draggable={false}
                                    />
                                )}
                            </div>
                        )}
                    </div>

                    <div className="vip-sim-wmod-side">
                        {slots.length > 0 ? (
                            <div className="vip-sim-wmod-equipped">
                                <h4 className="vip-sim-wmod-section-title">{t("simulator.wmod.equipped")}</h4>
                                <div className="vip-sim-wmod-slots">
                                    {slots.map((slot) => {
                                        const p = attachments[slot];
                                        const e = p ? getCatalogEntry(p.catalogId) : null;
                                        return (
                                            <EquippedSlot
                                                key={slot}
                                                slot={slot}
                                                placement={p}
                                                entry={e}
                                                onUnequip={onUnequipSlot}
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        ) : null}

                        <div className="vip-sim-wmod-pool">
                            <h4 className="vip-sim-wmod-section-title">{t("simulator.wmod.poolTitle")}</h4>
                            <p className="vip-sim-wmod-pool-hint">{t("simulator.wmod.poolHint")}</p>
                            {poolAttachments.length ? (
                                <div className="vip-sim-wmod-pool-grid">
                                    {poolAttachments.map(({ placement, entry: e }) => (
                                        <PoolAttachmentCard
                                            key={placement.instanceId}
                                            placement={placement}
                                            entry={e}
                                            equippedSlot={equippedByCatalogId[placement.catalogId]}
                                            onEquip={onEquipFromPool}
                                            onUnequipEquipped={onUnequipSlot}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <p className="vip-sim-wmod-pool-empty">{t("simulator.wmod.poolEmpty")}</p>
                            )}
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
